import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Rule from '@mui/icons-material/Rule';
import AttachMoney from '@mui/icons-material/AttachMoney';
import AccessTime from '@mui/icons-material/AccessTime';
import LocalShipping from '@mui/icons-material/LocalShipping';
import MenuBook from '@mui/icons-material/MenuBook';
import Inventory2 from '@mui/icons-material/Inventory2';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import {
  client, MarketOrderType, Instance, Market, MarketCatalogue, MarketItem,
} from '../../../app/services/api/orderManagementClient';
import { convertISODurationToTimeInput, convertTimeInputToISODuration, formatCutoffTimeForDisplay } from '../../../utils/timeUtils';
import { isOrderTypeDSD } from '../../../enums/OrderType';
import { applyRoundAndFormat } from '../../../utils';
import useLogError from '../../../hooks/useLogError';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';
import { DetailPageTemplate } from '../../../components/page-templates/detail-page/DetailPageTemplate';
import type { TabDefinition } from '../../../components/shared/tabs/TabbedSection';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import { TableSection, TableColumn } from '../../../components/shared/tables/TableSection';

const OrderChannelForm = () => {
  const { t } = useTranslation('orderTypes');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { instances, getMarkets, ensureMarketsLoaded } = useInstanceMarket();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);


  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketOrderType>({
    companyOrderTypeId: 0,
    instanceName: '',
    companyId: 0,
    orderTypeId: 0,
    name: '',
    marketName: '',
    customerTypeId: undefined,
    internal: false,
    external: false,
    orderingOnly: false,
    code: '',
    minimumOrderValue: 0,
    cutoff: {
      customerTypeOrderTypeId: undefined,
      orderCutOffTime: '',
      canPushOrderInEnteredDOS: false,
      supplierName: '',
      supplierEmailAddress: '',
      supplierEmailAddress2: '',
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Headline tiles/tabs are read-only summary data layered onto the existing form - they never affect save/update behaviour.
  const canViewChannelCatalogues = hasPermissionTo(['CatalogueView', 'ConfigurationAdmin']);
  const canEditCatalogues = hasPermissionTo(['CatalogueUpdate', 'ConfigurationAdmin']);
  const canViewChannelProducts = hasPermissionTo(['ProductView', 'ConfigurationAdmin']);

  const [marketCatalogues, setMarketCatalogues] = useState<MarketCatalogue[]>([]);
  const [isCataloguesLoading, setIsCataloguesLoading] = useState<boolean>(false);
  const [marketItems, setMarketItems] = useState<MarketItem[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState<boolean>(false);
  const [cataloguesPage, setCataloguesPage] = useState<number>(0);
  const [cataloguesSearchInput, setCataloguesSearchInput] = useState<string>('');

  const markets = getMarkets(formData.companyId ? formData.companyId.toString() : '');

  const channelCatalogues = useMemo(() => (
    canViewChannelCatalogues
      ? marketCatalogues.filter((catalogue) => (catalogue.orderTypeIds ?? []).includes(formData.orderTypeId))
      : []
  ), [canViewChannelCatalogues, marketCatalogues, formData.orderTypeId]);

  const channelProducts = useMemo(() => (
    canViewChannelProducts
      ? marketItems.filter((item) => (item.orderTypeIds ?? []).includes(formData.orderTypeId))
      : []
  ), [canViewChannelProducts, marketItems, formData.orderTypeId]);

  const filteredChannelCatalogues = useMemo(() => {
    const search = cataloguesSearchInput.trim().toLowerCase();
    if (!search) return channelCatalogues;
    return channelCatalogues.filter((catalogue) => catalogue.name?.toLowerCase().includes(search));
  }, [channelCatalogues, cataloguesSearchInput]);

  const cataloguesPageSize = 10;
  const cataloguesTotalPages = Math.max(1, Math.ceil(filteredChannelCatalogues.length / cataloguesPageSize));
  const pagedChannelCatalogues = filteredChannelCatalogues.slice(
    cataloguesPage * cataloguesPageSize,
    (cataloguesPage + 1) * cataloguesPageSize,
  );

  const selectedMarket = markets.find((market) => market.customerTypeId === formData.customerTypeId);

  useEffect(() => {
    if (!isEdit || !canViewChannelCatalogues || !formData.companyId || !formData.customerTypeId) return;
    setIsCataloguesLoading(true);
    client.getMarketCatalogues({
      companyId: formData.companyId.toString(),
      customerTypeId: formData.customerTypeId.toString(),
      searchTerm: '',
      sort: '',
      direction: '',
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          setMarketCatalogues(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching market catalogues:', error))
      .finally(() => setIsCataloguesLoading(false));
  }, [isEdit, canViewChannelCatalogues, formData.companyId, formData.customerTypeId]);

  useEffect(() => {
    if (!isEdit || !canViewChannelProducts || !formData.companyId || !formData.customerTypeId) return;
    setIsItemsLoading(true);
    client.getMarketItems({
      companyId: formData.companyId.toString(),
      customerTypeId: formData.customerTypeId.toString(),
      searchTerm: '',
      sort: '',
      direction: '',
      pageNumber: 0,
      pageSize: 2147483647,
    })
      .then((result) => {
        if (result.success && result.data) {
          setMarketItems(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching market items:', error))
      .finally(() => setIsItemsLoading(false));
  }, [isEdit, canViewChannelProducts, formData.companyId, formData.customerTypeId]);

  useEffect(() => {
    setCataloguesPage(0);
  }, [cataloguesSearchInput]);

  const normalizeOrderType = (data: MarketOrderType): MarketOrderType => ({
    ...data,
    name: data.name ?? '',
    instanceName: data.instanceName ?? '',
    marketName: data.marketName ?? '',
    code: data.code ?? '',
    internal: data.internal ?? false,
    external: data.external ?? false,
    orderingOnly: data.orderingOnly ?? false,
    minimumOrderValue: data.minimumOrderValue ?? 0,
    cutoff: {
      customerTypeOrderTypeId: data.cutoff?.customerTypeOrderTypeId ?? undefined,
      orderCutOffTime: data.cutoff?.orderCutOffTime ?? '',
      canPushOrderInEnteredDOS: data.cutoff?.canPushOrderInEnteredDOS ?? false,
      supplierName: data.cutoff?.supplierName ?? '',
      supplierEmailAddress: data.cutoff?.supplierEmailAddress ?? '',
      supplierEmailAddress2: data.cutoff?.supplierEmailAddress2 ?? '',
    },
  });


  const fetchOrderType = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await client.getMarketOrderType(id);
      if (result.success && result.data) {
        const normalized = normalizeOrderType(result.data);
        setFormData(normalized);
        if (result.data.companyId) {
          ensureMarketsLoaded(result.data.companyId.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching order type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchOrderType();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = t('nameRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    if (formData.minimumOrderValue !== undefined && formData.minimumOrderValue < 0) {
      newErrors.minimumOrderValue = t('minimumOrderValueMustBePositive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketOrderType) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'orderTypeId' || field === 'minimumOrderValue') {
      // Handle empty string to maintain the ability to clear the field
      value = value === '' ? 0 : parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleCutoffChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | boolean = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    if (field === 'orderCutOffTime' && typeof value === 'string') {
      const isoDuration = convertTimeInputToISODuration(value);
      value = isoDuration || '';
    }
    
    setFormData(prev => ({
      ...prev,
      cutoff: {
        ...prev.cutoff,
        [field]: value,
      },
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: undefined,
      marketName: '',
    }));
    
    if (value?.id) {
      ensureMarketsLoaded(value.id.toString());
    }
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId,
      marketName: value?.name || '',
    }));
  };

  const handleCheckboxChange = (field: keyof MarketOrderType) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketOrderType(id!, formData);
        if (result.success) {
          setMessage(t('procurementUpdateSuccessMessage', 'Order channel updated successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/order-channels${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createMarketOrderType(formData);
        if (result.success) {
          setMessage(t('procurementCreateSuccessMessage', 'Order channel created successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/order-channels${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit
        ? t('procurementUpdateErrorMessage', 'Failed to update order channel')
        : t('procurementCreateErrorMessage', 'Failed to create order channel')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/procurement/order-channels${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'OrderTypeUpdate' : 'OrderTypeCreate', 'ConfigurationAdmin']);
  const isDsdChannel = isOrderTypeDSD(formData.orderTypeId);
  const hasSupplierContact = Boolean(formData.cutoff?.supplierName || formData.cutoff?.supplierEmailAddress);

  const truncateText = (text: string, maxLength: number = 40) => {
    if (!text) return '';
    return text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;
  };

  const handleViewCatalogue = (catalogue: MarketCatalogue) => {
    if (!canEditCatalogues) return;
    const params = new URLSearchParams();
    params.set('companyId', formData.companyId.toString());
    if (formData.customerTypeId) params.set('customerTypeId', formData.customerTypeId.toString());
    navigate(`/procurement/catalogues/edit/${catalogue.customerGroupId}?${params.toString()}`);
  };

  const catalogueColumns: TableColumn<MarketCatalogue>[] = [
    {
      id: 'name',
      label: t('procurementCatalogueName', 'Catalogue'),
      render: (row) => (
        <Tooltip title={row.name}
          placement="top">
          <span>{truncateText(row.name)}</span>
        </Tooltip>
      ),
    },
  ];

  const overviewTabContent = (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('procurementOrderChannelName', 'Order Channel Name')}
          value={formData.name}
          onChange={handleInputChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth
          required
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('procurementOrderChannelId', 'Order Channel ID')}
          type="number"
          value={formData.orderTypeId !== undefined && formData.orderTypeId != null ? formData.orderTypeId.toString() : ''}
          onChange={handleInputChange('orderTypeId')}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('code')}
          value={formData.code ?? ''}
          onChange={handleInputChange('code')}
          error={!!errors.code}
          helperText={errors.code}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('minimumOrderValue')}
          type="number"
          value={formData.minimumOrderValue !== undefined && formData.minimumOrderValue != null ? formData.minimumOrderValue.toString() : ''}
          onChange={handleInputChange('minimumOrderValue')}
          error={!!errors.minimumOrderValue}
          helperText={errors.minimumOrderValue || t('minimumOrderValueHelp')}
          inputProps={{ min: 0 }}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Autocomplete
          options={instances}
          getOptionLabel={(option) => option.name}
          value={instances.find(instance => instance.id === formData.companyId) || null}
          onChange={handleInstanceChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('instance')}
              error={!!errors.companyId}
              helperText={errors.companyId}
              required
            />
          )}
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Autocomplete
          options={markets}
          getOptionLabel={(option) => option.name}
          value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
          onChange={handleMarketChange}
          disabled={!formData.companyId}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('market')}
              helperText={t('marketOptional')}
            />
          )}
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Stack
          direction="row"
          spacing={4}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.internal}
                onChange={handleCheckboxChange('internal')}
              />
            }
            label={t('internal')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.external}
                onChange={handleCheckboxChange('external')}
              />
            }
            label={t('external')}
          />
        </Stack>
      </Grid>
      <Grid item
        xs={12}
        md={6}
        sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.orderingOnly}
              onChange={handleCheckboxChange('orderingOnly')}
            />
          }
          label={t('orderingOnly')}
        />
      </Grid>
    </Grid>
  );

  const orderingTabContent = (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('orderCutoffTime')}
          type="time"
          value={convertISODurationToTimeInput(formData?.cutoff?.orderCutOffTime || '')}
          onChange={handleCutoffChange('orderCutOffTime')}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData?.cutoff?.canPushOrderInEnteredDOS || false}
              onChange={handleCutoffChange('canPushOrderInEnteredDOS')}
            />
          }
          label={t('canPushOrderInEnteredDOS')}
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('supplierName')}
          value={formData?.cutoff?.supplierName || ''}
          onChange={handleCutoffChange('supplierName')}
          error={!!errors.supplierName}
          helperText={errors.supplierName}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('supplierEmailAddress')}
          type="email"
          value={formData?.cutoff?.supplierEmailAddress || ''}
          onChange={handleCutoffChange('supplierEmailAddress')}
          error={!!errors.supplierEmailAddress}
          helperText={errors.supplierEmailAddress}
          fullWidth
        />
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <TextField
          label={t('supplierEmailAddress2')}
          type="email"
          value={formData?.cutoff?.supplierEmailAddress2 || ''}
          onChange={handleCutoffChange('supplierEmailAddress2')}
          fullWidth
        />
      </Grid>
    </Grid>
  );

  const cataloguesTabContent = !isEdit ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('procurementCataloguesAvailableAfterCreate', 'Catalogues using this order channel will show here once it has been created.')}
        </Typography>
      </Box>
    </Section>
  ) : !canViewChannelCatalogues ? (
    <Section style={{ mt: 0, p: 5 }}>
      <Box sx={{ p: { xs: 4, md: 5 } }}>
        <Typography variant="textSM"
          color="text.secondary">
          {t('procurementCataloguesPermissionRequired', 'You do not have permission to view catalogues.')}
        </Typography>
      </Box>
    </Section>
  ) : (
    <TableSection<MarketCatalogue>
      title={t('procurementOrderChannels', 'Catalogues')}
      columns={catalogueColumns}
      rows={pagedChannelCatalogues}
      getRowId={(row) => row.customerGroupId}
      onRowClick={canEditCatalogues ? handleViewCatalogue : undefined}
      recordCount={filteredChannelCatalogues.length}
      recordCountLabel={t('procurementCatalogues', 'Catalogues')}
      loading={isCataloguesLoading}
      searchValue={cataloguesSearchInput}
      onSearchChange={setCataloguesSearchInput}
      searchPlaceholder={t('procurementSearchCataloguesPlaceholder', 'Search catalogues...')}
      page={cataloguesPage}
      maxPages={cataloguesTotalPages}
      onPageChange={setCataloguesPage}
    />
  );

  const tabs: TabDefinition[] = [
    {
      id: 'overview',
      label: t('overview', 'Overview'),
      content: overviewTabContent,
    },
    {
      id: 'ordering',
      label: t('ordering', 'Ordering'),
      content: orderingTabContent,
    },
    {
      id: 'catalogues',
      label: t('procurementCatalogues', 'Catalogues'),
      content: cataloguesTabContent,
    },
  ];

  const channelTypeLabel = formData.internal && formData.external
    ? t('procurementChannelTypeBoth', 'Internal & External')
    : formData.internal
      ? t('internal', 'Internal')
      : formData.external
        ? t('external', 'External')
        : t('procurementChannelTypeNotSet', 'Not set');

  return (
    <DetailPageTemplate
      title={t(isEdit ? 'procurementEditOrderChannel' : 'procurementCreateOrderChannel', isEdit ? 'Edit Order Channel' : 'Create Order Channel')}
      subtitle={t('procurementOrderChannelFormSubtitle', 'Manage order channel setup, defaults, and cutoff details.')}
      loading={isLoading}
      loadingLabel={t('loading')}
      actions={(
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
          >
            {t('cancel')}
          </Button>
          {userCanSave && (
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
            </Button>
          )}
        </>
      )}
      headlineContent={{
        layout: 'six',
        insights: [
          <InsightTile
            key="channelType"
            label={t('procurementTileChannelTypeLabel', 'Channel Type')}
            value={channelTypeLabel}
            description={t('procurementTileChannelTypeDescription', 'Where this order channel can be used')}
            icon={<Rule />}
            variant="primary"
          />,
          <InsightTile
            key="minimumOrderValue"
            label={t('minimumOrderValue', 'Minimum Order Value')}
            value={formData.minimumOrderValue
              ? applyRoundAndFormat(formData.minimumOrderValue, selectedMarket?.currencyCode)
              : t('procurementChannelTypeNotSet', 'Not set')}
            description={t('procurementTileMinimumOrderValueDescription', 'Minimum value required to place an order')}
            icon={<AttachMoney />}
            variant="success"
          />,
          <InsightTile
            key="cutoffTime"
            label={t('orderCutoffTime', 'Cutoff Time')}
            value={formatCutoffTimeForDisplay(formData.cutoff?.orderCutOffTime) || t('procurementChannelTypeNotSet', 'Not set')}
            description={t('procurementTileCutoffDescription', 'Orders must be placed before this time')}
            icon={<AccessTime />}
            variant="secondary"
          />,
          <InsightTile
            key="dsd"
            label={t('procurementTileDsdLabel', 'DSD Channel')}
            value={isDsdChannel ? t('procurementTileDsdYes', 'DSD') : t('procurementTileDsdNo', 'Standard')}
            description={isDsdChannel
              ? (hasSupplierContact
                ? t('procurementTileDsdContactConfigured', 'Supplier contact configured')
                : t('procurementTileDsdContactMissing', 'No supplier contact set'))
              : t('procurementTileDsdContactNotRequired', 'No supplier contact required')}
            icon={<LocalShipping />}
            variant={isDsdChannel && !hasSupplierContact ? 'warning' : 'primary'}
          />,
          <InsightTile
            key="catalogues"
            label={t('procurementTileCataloguesLabel', 'Catalogues')}
            value={canViewChannelCatalogues ? channelCatalogues.length : '—'}
            description={t('procurementTileCataloguesDescription', 'Catalogues that include this order channel')}
            icon={<MenuBook />}
            variant="primary"
            loading={isEdit && canViewChannelCatalogues && isCataloguesLoading}
          />,
          <InsightTile
            key="products"
            label={t('procurementTileProductsLabel', 'Products')}
            value={canViewChannelProducts ? channelProducts.length : '—'}
            description={t('procurementTileProductsDescription', 'Products orderable through this channel')}
            icon={<Inventory2 />}
            variant="secondary"
            loading={isEdit && canViewChannelProducts && isItemsLoading}
          />,
        ],
      }}
      tabs={tabs}
      tabsAriaLabel={t('procurementOrderChannelName', 'Order Channel Name')}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </DetailPageTemplate>
  );
};

export default OrderChannelForm;
